import { Modal, Spin } from "antd";
import { ArrowRight, Clock, Search, Truck } from "lucide-react";
import { FC, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { TServiceTicket } from "~/types";
import { formatFullAddress, capitalize } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { ContainerInfoCard } from "~/components/common/container-info";
import { useNavigate } from "react-router-dom";

interface ServiceSelectorProps {
  services: TServiceTicket[];
  isLoading: boolean;
  selectedService: TServiceTicket | null;
  onSelectService: (service: TServiceTicket) => void;
  loadMore: () => void;
  hasMore: boolean;
}

export const ServiceSelector: FC<ServiceSelectorProps> = ({
  services,
  isLoading,
  selectedService,
  onSelectService,
  loadMore,
  hasMore,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [detailService, setDetailService] = useState<TServiceTicket | null>(null);
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg border h-full shadow-sm">
      <div className="p-4 border-b font-medium">Select Service</div>
      <div className="p-4">
        <div className="relative mb-4">
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
        </div>

        <Spin spinning={isLoading && services.length === 0}>
          <InfiniteScroll
            dataLength={services.length}
            next={loadMore}
            hasMore={hasMore}
            loader={<Spin className="m-5" />}
            height="55vh"
            endMessage={
              <p className="text-center text-gray-500 mt-4">
                No more services to load
              </p>
            }
          >
            {services.map((service) => (
              <div
                key={service.id}
                className={`p-3 border rounded-lg cursor-pointer m-2 ${selectedService?.id === service.id
                  ? "border-blue-500 bg-blue-50"
                  : "hover:bg-gray-50"
                  }`}
                onClick={() => onSelectService(service)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setDetailService(service);
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium">{service.serial}</span>
                  <div className="flex flex-col items-end text-gray-500 text-xs">
                    <div className="flex items-center space-x-1">
                      <Clock size={12} />
                      <span>
                        {(service.serviceDatetime || service.startDatetime)
                          ? new Date(service.serviceDatetime || service.startDatetime).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            })
                          : "N/A"}
                      </span>
                    </div>
                    <span className="font-medium">
                      {service.startDatetime
                        ? new Date(service.startDatetime).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })
                        : "N/A"}
                    </span>
                    {service.duration && (
                      <span className="text-gray-400">
                        {service.duration}h
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm mb-2 font-medium text-gray-700">
                  {capitalize(service.serviceType.replace(/_/g, ' '))}
                </div>
                <div className="text-sm mb-2">
                  <span className="font-medium text-gray-700">
                    {service.client?.companyName || "Unnamed Client"}
                  </span>
                  {service.jobTicket?.client?.id !== service.client?.id && service.jobTicket?.client?.companyName && (
                    <span className="italic text-gray-500">
                      {" "}({service.jobTicket.client.companyName})
                    </span>
                  )}
                </div>
                {service.vessel && (
                  <div className="flex items-center space-x-1 text-gray-600 text-sm mb-2">
                    <Truck size={14} />
                    <span>{service.vessel.name} - {service.vessel.vesselRef}</span>
                  </div>
                )}
                <div className="flex items-center space-x-1 text-gray-600 text-sm">
                  <ArrowRight size={14} />
                  <span>
                    {formatFullAddress(service?.location) || "No address"}
                  </span>
                </div>
                <div className="mt-2 flex items-center space-x-2">
                  {service?.staffs?.slice(0, 2).map((staff) => (
                    <div
                      key={staff.id}
                      className="text-xs px-2 py-1 bg-gray-100 rounded-full"
                    >
                      {formatStaffName(staff)}
                    </div>
                  ))}
                  {service?.staffs && service.staffs.length > 2 && (
                    <div className="text-xs px-2 py-1 bg-gray-200 rounded-full text-gray-600">
                      +{service.staffs.length - 2}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </InfiniteScroll>
        </Spin>
      </div>

      {/* Service Detail Modal */}
      <Modal
        title="Service Details"
        open={!!detailService}
        onCancel={() => setDetailService(null)}
        footer={null}
        width={800}
      >
        {detailService && (
          <div
            className="cursor-pointer"
            onClick={() => {
              const bookingId = detailService.jobTicketId || detailService.jobTicket?.id;
              if (bookingId) {
                navigate(`/bookings/${bookingId}?tab=services&serviceId=${detailService.id}`);
              }
            }}
          >
            <ContainerInfoCard
              serviceTicket={detailService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
